import {convertColumnNameToPropertyName} from "../../logic3d/managers/ClipboardUtils";
import type {AppActions} from "../../../../../../data/state/AppActions";
import {XyiconFeature} from "../../../../../../generated/api/base";
import type {Markup} from "../../../../../../data/models/Markup";
import type {BoundarySpaceMap} from "../../../../../../data/models/BoundarySpaceMap";
import type {Xyicon} from "../../../../../../data/models/Xyicon";
import type {IFieldAdapter} from "../../../../../../data/models/field/Field";
import {featureTitles} from "../../../../../../data/state/AppStateConstants";
import {colorPalette} from "../../../../../5.0/styles/colorPalette";

export class HTMLTableGenerator {
	private static clearStyle(style: CSSStyleDeclaration) {
		style.fontFamily = "initial";
		style.color = "initial";
		style.fontSize = "initial";
	}

	private static createTableElement() {
		const table = document.createElement("table");

		this.clearStyle(table.style);
		table.setAttribute("cellspacing", "0");
		table.setAttribute("cellpadding", "0");
		table.setAttribute("border", "1");
		table.style.borderCollapse = "collapse";

		return table;
	}

	private static createTrElement() {
		return document.createElement("tr");
	}

	private static createThElement(text: string) {
		const th = document.createElement("th");

		th.textContent = text;

		return th;
	}

	private static createTdElement(value: string, containsHTML = false) {
		const td = document.createElement("td");

		this.clearStyle(td.style);
		if (containsHTML) {
			td.innerHTML = value ?? "";
		} else {
			td.textContent = value ?? "";
		}

		return td;
	}

	private static putColumnValueIntoTable(spaceItem: Xyicon | BoundarySpaceMap | Markup, columnName: string, tr: HTMLTableRowElement) {
		const propertyName = convertColumnNameToPropertyName(columnName) as keyof (Xyicon | BoundarySpaceMap | Markup);
		let value: unknown = spaceItem[propertyName] ?? "";
		if (propertyName === "misc") {
			value = {
				copyOf: spaceItem.id,
			};
		}
		let strValue: string = typeof value === "object" ? JSON.stringify(value) : `${value}`;

		if (strValue === "[]") {
			strValue = "";
		}
		const td = this.createTdElement(strValue);

		tr.appendChild(td);
	}

	public static createTableForSpaceItems(spaceItems: Xyicon[] | BoundarySpaceMap[] | Markup[], actions: AppActions) {
		if (spaceItems.length > 0) {
			const feature = spaceItems[0].ownFeature;
			const isXyiconOrBoundary = [XyiconFeature.Xyicon, XyiconFeature.Boundary].includes(feature);

			const fields: IFieldAdapter[] = isXyiconOrBoundary ? actions.getAssignedFieldsFromLayoutForItems(spaceItems) : [];
			let tableSpaceItems = spaceItems;

			if (feature === XyiconFeature.Xyicon) {
				const embeddedXyicons: Xyicon[] = [];

				for (const spaceItem of spaceItems) {
					embeddedXyicons.push(...(spaceItem as Xyicon).embeddedXyicons);
				}
				//Xyicon[] is not working
				tableSpaceItems = [...(spaceItems as Xyicon[]), ...embeddedXyicons];
			}

			const table = this.createTableElement();
			const tableHeader = this.createTrElement();

			tableHeader.style.backgroundColor = colorPalette.primary.c500Primary;

			const spaceItemTitle = featureTitles[feature];

			// before the "fields"
			const preColumns: string[] = [];
			// after the "fields"
			const postColumns: string[] = [];

			if (feature === XyiconFeature.Xyicon) {
				preColumns.push(`${spaceItemTitle} Model`);
			}
			preColumns.push(`${spaceItemTitle} Type`);

			switch (feature) {
				case XyiconFeature.Xyicon:
					postColumns.push(
						`${spaceItemTitle} ID`,
						`${spaceItemTitle} Space ID`,
						`Parent ${spaceItemTitle} ID`,
						`${spaceItemTitle} X`,
						`${spaceItemTitle} Y`,
						`${spaceItemTitle} Z`,
						`${spaceItemTitle} Orientation`,
						`${spaceItemTitle} Port Data`,
						`${spaceItemTitle} Settings`,
					);
					break;
				case XyiconFeature.Boundary:
					postColumns.push(`${spaceItemTitle} ID`, `${spaceItemTitle} Geometry`, `${spaceItemTitle} Orientation`, `${spaceItemTitle} Misc`);
					break;
				case XyiconFeature.Markup:
					postColumns.push(
						`${spaceItemTitle} Geometry`,
						`${spaceItemTitle} Fill Transparency`,
						`${spaceItemTitle} Line Thickness`,
						`${spaceItemTitle} Settings`,
						`${spaceItemTitle} Text`,
						`${spaceItemTitle} Orientation`,
						`${spaceItemTitle} Color`,
						`${spaceItemTitle} Misc`,
					);
					break;
			}

			for (const additionalColumn of preColumns) {
				tableHeader.appendChild(this.createThElement(additionalColumn));
			}

			for (const field of fields) {
				tableHeader.appendChild(this.createThElement(field.name));
			}

			for (const additionalColumn of postColumns) {
				tableHeader.appendChild(this.createThElement(additionalColumn));
			}

			table.appendChild(tableHeader);

			for (const spaceItem of tableSpaceItems) {
				const tr = this.createTrElement();

				for (let i = 0; i < preColumns.length; ++i) {
					const additionalColumn = preColumns[i];

					this.putColumnValueIntoTable(spaceItem, additionalColumn, tr);
				}

				if (isXyiconOrBoundary) {
					for (const field of fields) {
						const fieldValue: string | boolean = actions.getHumanFriendlyFieldValueForItem(spaceItem, field, false);

						const td = this.createTdElement(fieldValue, true);

						tr.appendChild(td);
					}
				}

				for (const additionalColumn of postColumns) {
					this.putColumnValueIntoTable(spaceItem, additionalColumn, tr);
				}

				table.appendChild(tr);
			}

			return table;
		} else {
			return null;
		}
	}
}
